import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Text } from "../../components/ui/Text";
import { api } from "../../services/api";
import { FaPlus, FaTrash, FaArrowLeft } from "react-icons/fa";

interface Contributor {
  _id: string;
  name: string;
  email?: string;
}

interface Role {
  _id: string;
  name: string;
  description?: string;
}

interface Location {
  _id: string;
  country: string;
  countryCode: string;
  currency: string;
}

interface ContributorAssignment {
  contributorId: string;
  roleId: string;
}

interface Product {
  _id: string;
  title: string;
  materialCost?: number;
  contributorAssignments?: ContributorAssignment[];
  locationPricing?: Array<{
    locationId: string;
    price?: number;
    inventoryQuantity?: number;
    materialCost?: number;
    isAvailable: boolean;
  }>;
  variantOptions?: Array<{
    _id?: string;
    name: string;
    values: Array<{
      value: string;
      materialCost?: number;
      locationPricing?: Array<{
        locationId: string;
        materialCost?: number;
      }>;
    }>;
  }>;
  variantCombinations?: Array<{
    combination: string;
    materialCost?: number;
    locationPricing?: Array<{
      locationId: string;
      materialCost?: number;
    }>;
  }>;
  variants?: Array<{
    _id: string;
    name: string;
    materialCost?: number;
  }>;
}

export const ProductProfitSettings: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [product, setProduct] = useState<Product | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  // Material cost state
  const [rootMaterialCost, setRootMaterialCost] = useState<number | undefined>(undefined);
  const [locationMaterialCosts, setLocationMaterialCosts] = useState<Record<string, number | undefined>>({});
  const [variantMaterialCosts, setVariantMaterialCosts] = useState<Record<string, number | undefined>>({});
  const [variantOptionValueMaterialCosts, setVariantOptionValueMaterialCosts] = useState<Record<string, number | undefined>>({});
  const [variantCombinationMaterialCosts, setVariantCombinationMaterialCosts] = useState<Record<string, number | undefined>>({});

  // Contributor assignments state
  const [contributorAssignments, setContributorAssignments] = useState<ContributorAssignment[]>([]);

  useEffect(() => {
    if (productId) {
      loadData();
    }
  }, [productId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [productResponse, contributorsResponse, rolesResponse, locationsResponse] = await Promise.all([
        api.get(`/products/${productId}`),
        api.get("/profit-rules/contributors"),
        api.get("/profit-rules/roles"),
        api.get("/locations"),
      ]);

      const productData = productResponse.data;
      setProduct(productData);
      setRootMaterialCost(productData.materialCost);
      setContributorAssignments(productData.contributorAssignments || []);
      setContributors(contributorsResponse.data);
      setRoles(rolesResponse.data);
      setLocations(locationsResponse.data);

      // Load location material costs
      if (productData.locationPricing) {
        const locationCosts: Record<string, number | undefined> = {};
        productData.locationPricing.forEach((lp: any) => {
          if (lp.materialCost !== undefined) {
            locationCosts[lp.locationId] = lp.materialCost;
          }
        });
        setLocationMaterialCosts(locationCosts);
      }

      // Load variant material costs
      if (productData.variants) {
        const variantCosts: Record<string, number | undefined> = {};
        productData.variants.forEach((v: any) => {
          if (v.materialCost !== undefined) {
            variantCosts[v._id] = v.materialCost;
          }
        });
        setVariantMaterialCosts(variantCosts);
      }

      // Load variant option value material costs
      if (productData.variantOptions) {
        const optionValueCosts: Record<string, number | undefined> = {};
        productData.variantOptions.forEach((option: any, optIndex: number) => {
          option.values.forEach((value: any, valIndex: number) => {
            const key = `${optIndex}-${valIndex}`;
            if (value.materialCost !== undefined) {
              optionValueCosts[key] = value.materialCost;
            }
          });
        });
        setVariantOptionValueMaterialCosts(optionValueCosts);
      }

      // Load variant combination material costs
      if (productData.variantCombinations) {
        const combinationCosts: Record<string, number | undefined> = {};
        productData.variantCombinations.forEach((combo: any) => {
          if (combo.materialCost !== undefined) {
            combinationCosts[combo.combination] = combo.materialCost;
          }
        });
        setVariantCombinationMaterialCosts(combinationCosts);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load product data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!productId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Prepare update payload
      const updateData: any = {
        materialCost: rootMaterialCost,
        contributorAssignments: contributorAssignments.filter((a) => a.contributorId && a.roleId),
      };

      // Update location pricing with material costs
      if (product?.locationPricing) {
        updateData.locationPricing = product.locationPricing.map((lp: any) => ({
          ...lp,
          materialCost: locationMaterialCosts[lp.locationId] || undefined,
        }));
      }

      // Update variants with material costs
      if (product?.variants) {
        updateData.variants = product.variants.map((v: any) => ({
          ...v,
          materialCost: variantMaterialCosts[v._id] || undefined,
        }));
      }

      // Update variant options with material costs
      if (product?.variantOptions) {
        updateData.variantOptions = product.variantOptions.map((option: any, optIndex: number) => ({
          ...option,
          values: option.values.map((value: any, valIndex: number) => {
            const key = `${optIndex}-${valIndex}`;
            return {
              ...value,
              materialCost: variantOptionValueMaterialCosts[key] || undefined,
            };
          }),
        }));
      }

      // Update variant combinations with material costs
      if (product?.variantCombinations) {
        updateData.variantCombinations = product.variantCombinations.map((combo: any) => ({
          ...combo,
          materialCost: variantCombinationMaterialCosts[combo.combination] || undefined,
        }));
      }

      await api.patch(`/products/${productId}`, updateData);
      setSuccess("Product profit settings saved successfully");
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to save product profit settings");
    } finally {
      setSaving(false);
    }
  };

  const addContributorAssignment = () => {
    setContributorAssignments([...contributorAssignments, { contributorId: "", roleId: "" }]);
  };

  const removeContributorAssignment = (index: number) => {
    setContributorAssignments(contributorAssignments.filter((_, i) => i !== index));
  };

  const updateContributorAssignment = (index: number, field: "contributorId" | "roleId", value: string) => {
    const updated = [...contributorAssignments];
    updated[index] = { ...updated[index], [field]: value };
    setContributorAssignments(updated);
  };

  if (loading) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <Alert variant="error">{t("admin.productProfitSettings.productNotFound")}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <div className="flex items-center justify-between">
          <div>
            <Heading size="xl">{t("admin.productProfitSettings.title")}</Heading>
            <Text className="text-gray-600 dark:text-gray-400 mt-2">
              {t("admin.productProfitSettings.description", { productTitle: product.title })}
            </Text>
          </div>
          <Button variant="outline" onClick={() => navigate(`/admin/products/${productId}`)}>
            <FaArrowLeft className="mr-2" />
            {t("admin.productProfitSettings.backToProduct")}
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Material Costs Section */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.productProfitSettings.materialCosts.title")}</Heading>
            <Text className="text-gray-600 dark:text-gray-400 text-sm">
              {t("admin.productProfitSettings.materialCosts.description")}
            </Text>

            {/* Root Level Material Cost */}
            <div>
              <Input
                label={t("admin.productProfitSettings.materialCosts.rootLevel")}
                type="number"
                step="0.01"
                min="0"
                value={rootMaterialCost || ""}
                onChange={(e) => setRootMaterialCost(e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>

            {/* Location Material Costs */}
            {locations.length > 0 && (
              <div>
                <Heading size="sm" className="mb-2">{t("admin.productProfitSettings.materialCosts.perCountry")}</Heading>
                <div className="space-y-2">
                  {locations.map((loc) => (
                    <div key={loc._id} className="flex items-center gap-2">
                      <Text className="w-32">{loc.country}:</Text>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={locationMaterialCosts[loc._id] || ""}
                        onChange={(e) =>
                          setLocationMaterialCosts({
                            ...locationMaterialCosts,
                            [loc._id]: e.target.value ? parseFloat(e.target.value) : undefined,
                          })
                        }
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Variant Material Costs */}
            {product.variants && product.variants.length > 0 && (
              <div>
                <Heading size="sm" className="mb-2">{t("admin.productProfitSettings.materialCosts.perVariant")}</Heading>
                <div className="space-y-2">
                  {product.variants.map((variant) => (
                    <div key={variant._id} className="flex items-center gap-2">
                      <Text className="w-32">{variant.name}:</Text>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={variantMaterialCosts[variant._id] || ""}
                        onChange={(e) =>
                          setVariantMaterialCosts({
                            ...variantMaterialCosts,
                            [variant._id]: e.target.value ? parseFloat(e.target.value) : undefined,
                          })
                        }
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Variant Option Value Material Costs */}
            {product.variantOptions && product.variantOptions.length > 0 && (
              <div>
                <Heading size="sm" className="mb-2">{t("admin.productProfitSettings.materialCosts.perVariantOptionValue")}</Heading>
                <div className="space-y-4">
                  {product.variantOptions.map((option, optIndex) => (
                    <div key={optIndex}>
                      <Text className="font-medium mb-2">{option.name}:</Text>
                      <div className="space-y-2 ml-4">
                        {option.values.map((value, valIndex) => {
                          const key = `${optIndex}-${valIndex}`;
                          return (
                            <div key={valIndex} className="flex items-center gap-2">
                              <Text className="w-32">{value.value}:</Text>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={variantOptionValueMaterialCosts[key] || ""}
                                onChange={(e) =>
                                  setVariantOptionValueMaterialCosts({
                                    ...variantOptionValueMaterialCosts,
                                    [key]: e.target.value ? parseFloat(e.target.value) : undefined,
                                  })
                                }
                                className="flex-1"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Variant Combination Material Costs */}
            {product.variantCombinations && product.variantCombinations.length > 0 && (
              <div>
                <Heading size="sm" className="mb-2">{t("admin.productProfitSettings.materialCosts.perVariantCombination")}</Heading>
                <div className="space-y-2">
                  {product.variantCombinations.map((combo) => (
                    <div key={combo.combination} className="flex items-center gap-2">
                      <Text className="w-64 text-sm">{combo.combination}:</Text>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={variantCombinationMaterialCosts[combo.combination] || ""}
                        onChange={(e) =>
                          setVariantCombinationMaterialCosts({
                            ...variantCombinationMaterialCosts,
                            [combo.combination]: e.target.value ? parseFloat(e.target.value) : undefined,
                          })
                        }
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Stack>
        </Card>

        {/* Contributor Assignments Section */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.productProfitSettings.contributorAssignments.title")}</Heading>
            <Text className="text-gray-600 dark:text-gray-400 text-sm">
              {t("admin.productProfitSettings.contributorAssignments.description")}
            </Text>

            <div className="space-y-3">
              {contributorAssignments.map((assignment, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <Select
                    label={t("admin.productProfitSettings.selectContributor")}
                    value={assignment.contributorId}
                    onChange={(e) => updateContributorAssignment(index, "contributorId", e.target.value)}
                    className="flex-1"
                    options={[
                      { value: "", label: t("admin.productProfitSettings.selectContributor") },
                      ...contributors.map((c) => ({
                        value: c._id,
                        label: c.name,
                      })),
                    ]}
                  />
                  <Select
                    label={t("admin.productProfitSettings.selectRole")}
                    value={assignment.roleId}
                    onChange={(e) => updateContributorAssignment(index, "roleId", e.target.value)}
                    className="flex-1"
                    options={[
                      { value: "", label: t("admin.productProfitSettings.selectRole") },
                      ...roles.map((r) => ({
                        value: r._id,
                        label: r.name,
                      })),
                    ]}
                  />
                  <Button
                    variant="outline"
                    onClick={() => removeContributorAssignment(index)}
                    disabled={saving}
                  >
                    <FaTrash />
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" onClick={addContributorAssignment} disabled={saving}>
              <FaPlus className="mr-2" />
              {t("admin.productProfitSettings.addAssignment")}
            </Button>
          </Stack>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(`/admin/products/${productId}`)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </Stack>
    </Container>
  );
};

